import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { PartnerTokenTierConfig } from '../types/partnerToken';
import { partnerTokenService, DEFAULT_PARTNER_TOKEN_TIERS } from '../services/partnerTokenService';
import { useAuth } from './AuthContext';

interface PartnerTokenTiersContextValue {
  tiers: PartnerTokenTierConfig[];
  tokenTitles: Record<string, string>;
  tokenLetters: Record<string, string>;
  tokenDescriptions: Record<string, string>;
  tokenOptions: Array<{ tier: string; title: string; description: string }>;
  loading: boolean;
  refresh: () => Promise<void>;
}

const defaultTitles: Record<string, string> = {
  simple: 'Prepared',
  gold: 'Prepared+',
  premium: 'Executive',
};

const defaultLetters: Record<string, string> = {
  simple: 'S',
  gold: 'G',
  premium: 'P',
};

const defaultDescriptions: Record<string, string> = {
  simple: 'Create your own virtual tabletop scenario and complete on your own time.',
  gold: 'Create your own virtual tabletop scenario and submit your results for an after-action ISO 22301 compliance assessment. When the client uses the token, Flash is notified to begin the assessment.',
  premium: 'Create your own virtual tabletop scenario and schedule professional facilitation by a Flash AI facilitator. When the client uses the token, Flash AI is notified to coordinate facilitation. Facilitation is not an ISO certification.',
};

const PartnerTokenTiersContext = createContext<PartnerTokenTiersContextValue>({
  tiers: DEFAULT_PARTNER_TOKEN_TIERS,
  tokenTitles: defaultTitles,
  tokenLetters: defaultLetters,
  tokenDescriptions: defaultDescriptions,
  tokenOptions: DEFAULT_PARTNER_TOKEN_TIERS.map((t) => ({ tier: t.tier, title: t.name, description: t.description || '' })),
  loading: false,
  refresh: async () => {},
});

function useAuthSafe() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useAuth();
  } catch {
    return null;
  }
}

export const PartnerTokenTiersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tiers, setTiers] = useState<PartnerTokenTierConfig[]>(DEFAULT_PARTNER_TOKEN_TIERS);
  const [loading, setLoading] = useState<boolean>(true);
  const auth = useAuthSafe();

  const fetchTiers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await partnerTokenService.getPartnerTokenTiers();
      if (Array.isArray(data) && data.length > 0) {
        setTiers(data);
      }
    } catch (e) {
      console.warn('Failed to fetch partner token tiers:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers, auth?.isAuthenticated, auth?.user?.id]);

  useEffect(() => {
    const handleFocus = () => {
      fetchTiers();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchTiers]);

  const tokenTitles = useMemo(() => {
    const map: Record<string, string> = { ...defaultTitles };
    tiers.forEach((t) => {
      if (t.name) map[t.tier] = t.name;
    });
    return map;
  }, [tiers]);

  const tokenLetters = useMemo(() => {
    const map: Record<string, string> = { ...defaultLetters };
    tiers.forEach((t) => {
      if (t.badge_letter) map[t.tier] = t.badge_letter;
      else if (t.name) map[t.tier] = t.name.charAt(0).toUpperCase();
    });
    return map;
  }, [tiers]);

  const tokenDescriptions = useMemo(() => {
    const map: Record<string, string> = { ...defaultDescriptions };
    tiers.forEach((t) => {
      if (t.description) map[t.tier] = t.description;
    });
    return map;
  }, [tiers]);

  const tokenOptions = useMemo(() => {
    return tiers.map((t) => ({
      tier: t.tier,
      title: t.name || defaultTitles[t.tier] || t.tier,
      description: t.description || defaultDescriptions[t.tier] || '',
    }));
  }, [tiers]);

  return (
    <PartnerTokenTiersContext.Provider
      value={{
        tiers,
        tokenTitles,
        tokenLetters,
        tokenDescriptions,
        tokenOptions,
        loading,
        refresh: fetchTiers,
      }}
    >
      {children}
    </PartnerTokenTiersContext.Provider>
  );
};

export const usePartnerTokenTiers = () => useContext(PartnerTokenTiersContext);
